'use client';

import React, { useState } from 'react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';

const serviceTypes = [
{
  title: 'Architects',
  img: 'https://www.nexsys.group/assets/platform-hero-ZEjjU6lr.png',
  intro: 'Architectural partners integrate NeXSYS into building designs, ensuring spatial efficiency, compliance with planning regulations, and alignment with modular construction principles from concept through delivery.',
  benefits: [
  'Seamless integration with modular construction principles.',
  'Enhanced design efficiency with pre-engineered systems.',
  'Assurance of compliance with local planning and building codes.',
  'Access to NeXSYS training for system design and detailing.',
  'Support in innovative multi-storey or complex projects.']

},
{
  title: 'Structural Engineers',
  img: 'https://www.nexsys.group/assets/modular-frame-system-DQvOvZwE.png',
  intro: 'Structural engineers collaborate to adapt NeXSYS framing systems to local codes, load requirements, and multi-storey applications, ensuring safety, efficiency, and structural integrity.',
  benefits: [
  'Reduced design iteration through pre-engineered system parameters.',
  'Confidence in multi-storey and complex load-bearing applications.',
  'Compliance with safety and structural standards.',
  'Access to NeXSYS-certified training for structural integration.',
  'Streamlined collaboration with architects and MEP engineers.']

},
{
  title: 'MEP Engineers',
  img: 'https://www.nexsys.group/assets/mep-wall-inserts-CaTRF6Xw.png',
  intro: 'MEP engineers coordinate mechanical, electrical, and plumbing systems within the NeXSYS platform, optimising routing, clash-free integration, and compliance with performance standards.',
  benefits: [
  'Efficient system integration with minimal rework.',
  'Reduced on-site installation conflicts through pre-planning.',
  'Optimized routing within modular frames.',
  'Training on NeXSYS MEP coordination best practices.',
  'Improved project delivery timelines and quality.']

},
{
  title: 'Fire & Safety Engineers',
  img: 'https://www.nexsys.group/assets/benefits-hero-DMTBAPLn.jpg',
  intro: 'Fire and safety consultants validate fire ratings, egress strategies, and life-safety compliance for buildings delivered using NeXSYS systems across jurisdictions.',
  benefits: [
  'Assurance of system compliance with local fire and safety codes.',
  'Risk mitigation for multi-storey and complex structures.',
  'Support for certification processes and approvals.',
  'Access to NeXSYS guidance and certification procedures.',
  'Confidence in safety performance of modular installations.']

},
{
  title: 'Other Consultants',
  img: 'https://www.nexsys.group/assets/platform-diagram-B3ZFAG8x.png',
  intro: 'This includes acoustic, façade, sustainability, cost, and code consultants who support project delivery and certification when deploying NeXSYS across different markets.',
  benefits: [
  'Holistic project compliance and performance assurance.',
  'Optimized cost and material usage.',
  'Streamlined sustainability certification processes.',
  'Access to NeXSYS reference data and training.',
  'Better coordination across project disciplines.']

}];


const supportItems = [
'Exclusive territory rights',
'Comprehensive training programs',
'Technical engineering support',
'Marketing and sales materials',
'Lead sharing and referrals',
'Co-branded marketing campaigns',
'Priority product access',
'Annual partner summit'];


export default function ProfessionalServicesPage() {
  const [formData, setFormData] = useState({ name: '', company: '', email: '', type: 'Select Service Type' });
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');

  const handleSubmit = async () => {
    if (!formData?.name || !formData?.company || !formData?.email || formData?.type === 'Select Service Type') {
      setStatus('error');
      return;
    }
    setStatus('loading');
    try {
      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fullName: formData?.name,
          company: formData?.company,
          email: formData?.email,
          projectType: formData?.type,
          message: `Partner enquiry from Professional Services page. Service Type: ${formData?.type}`,
        }),
      });
      if (res?.ok) {
        setStatus('success');
        setFormData({ name: '', company: '', email: '', type: 'Select Service Type' });
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-grow">
        {/* Hero Section */}
        <section className="pt-24 pb-12 bg-gradient-dark">
          <div className="container mx-auto container-padding">
            <div className="max-w-3xl">
              <span className="text-primary font-semibold uppercase tracking-wider text-sm">Professional Services</span>
              <h1 className="text-hero lg:text-hero-lg font-heading font-bold text-white mt-2 mb-6">
                Partner with <span className="font-bold text-white">NeXSYS</span>
              </h1>
              <p className="text-xl text-white/80 leading-relaxed">
                Join our global network of licensed &amp; Certified Partners transforming construction markets worldwide.
              </p>
            </div>
          </div>
        </section>

        {/* Intro Section */}
        <section className="section-padding bg-card">
          <div className="container mx-auto container-padding max-w-4xl">
            <h2 className="text-section lg:text-section-lg font-heading font-bold text-foreground mb-6">
              Design and Deliver with Confidence
            </h2>
            <p className="text-lg text-muted-foreground leading-relaxed">
              We collaborate with architects, structural engineers, MEP engineers, fire consultants, and other professional services trained in the <span className="font-bold text-nexsys-brand">NeXSYS</span> system to deliver rapid, high-quality project outcomes. Our certified professional partners bring deep expertise in modular design, code compliance, and integrated project delivery—ensuring every building meets the highest standards from concept through completion.
            </p>
          </div>
        </section>

        {/* Partnership Opportunities */}
        <section className="section-padding">
          <div className="container mx-auto container-padding">
            <div className="mb-12 lg:mb-16 text-center">
              <h2 className="text-section lg:text-section-lg font-heading font-bold text-foreground mb-4">
                Partnership Opportunities
              </h2>
              <p className="text-lg text-muted-foreground">
                Multiple pathways to partner with <span className="font-bold text-nexsys-brand">NeXSYS</span>.
              </p>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {serviceTypes?.map((type) =>
              <div key={type?.title} className="bg-card rounded-xl overflow-hidden shadow-card border border-border">
                  <div className="p-6">
                    <h3 className="font-heading font-bold text-lg text-foreground mb-3">{type?.title}</h3>
                    <p className="text-muted-foreground text-sm mb-4 leading-relaxed">
                      {type?.intro?.split('NeXSYS')?.map((part, i, arr) =>
                    <React.Fragment key={i}>
                          {part}
                          {i < arr?.length - 1 && <span className="font-bold text-nexsys-brand">NeXSYS</span>}
                        </React.Fragment>
                    )}
                    </p>
                    <p className="font-semibold text-foreground text-sm mb-2">Benefits:</p>
                    <ul className="space-y-2">
                      {type?.benefits?.map((b) =>
                    <li key={b} className="flex items-start gap-2 text-sm text-muted-foreground">
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-primary flex-shrink-0 mt-0.5">
                            <path d="M20 6 9 17l-5-5" />
                          </svg>
                          {b?.split('NeXSYS')?.map((part, i, arr) =>
                      <React.Fragment key={i}>
                              {part}
                              {i < arr?.length - 1 && <span className="font-bold text-nexsys-brand">NeXSYS</span>}
                            </React.Fragment>
                      )}
                        </li>
                    )}
                    </ul>
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Support Section */}
        <section className="section-padding bg-nexsys-surface">
          <div className="container mx-auto container-padding">
            <div className="mb-12 text-center">
              <h2 className="text-section lg:text-section-lg font-heading font-bold text-foreground mb-4">
                Professional Services Partner Support
              </h2>
              <p className="text-lg text-muted-foreground">We invest in our partners&apos; success.</p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {supportItems?.map((item) =>
              <div key={item} className="bg-card rounded-xl p-4 shadow-card border border-border flex items-center gap-3">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-primary flex-shrink-0">
                    <path d="M20 6 9 17l-5-5" />
                  </svg>
                  <span className="text-sm font-medium text-foreground">{item}</span>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Contact Form */}
        <section className="section-padding bg-gradient-dark">
          <div className="container mx-auto container-padding">
            <div className="max-w-xl mx-auto">
              <h3 className="text-2xl font-heading font-bold text-white mb-8 text-center">
                Become a Professional Services Partner
              </h3>
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
                {status === 'success' ? (
                  <div className="text-center py-6">
                    <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-primary mx-auto mb-4">
                      <path d="M20 6 9 17l-5-5" />
                    </svg>
                    <p className="text-white text-lg font-semibold">Thank you! We&apos;ll be in touch soon.</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <input
                      type="text"
                      placeholder="Name"
                      value={formData?.name}
                      onChange={(e) => setFormData({ ...formData, name: e?.target?.value })}
                      className="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder-white/50 focus:outline-none focus:border-primary" />
                    <input
                      type="text"
                      placeholder="Company"
                      value={formData?.company}
                      onChange={(e) => setFormData({ ...formData, company: e?.target?.value })}
                      className="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder-white/50 focus:outline-none focus:border-primary" />
                    <input
                      type="email"
                      placeholder="Email"
                      value={formData?.email}
                      onChange={(e) => setFormData({ ...formData, email: e?.target?.value })}
                      className="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder-white/50 focus:outline-none focus:border-primary" />
                    <select
                      value={formData?.type}
                      onChange={(e) => setFormData({ ...formData, type: e?.target?.value })}
                      className="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white focus:outline-none focus:border-primary">
                      <option value="Select Service Type">Select Service Type</option>
                      <option value="Architects">Architects</option>
                      <option value="Structural Engineers">Structural Engineers</option>
                      <option value="MEP Engineers">MEP Engineers</option>
                      <option value="Fire & Safety Engineers">Fire &amp; Safety Engineers</option>
                      <option value="Other Consultants">Other Consultants</option>
                    </select>
                    {status === 'error' && (
                      <p className="text-red-400 text-sm">Please fill in all fields and select a service type.</p>
                    )}
                    <button
                      onClick={handleSubmit}
                      disabled={status === 'loading'}
                      className="w-full bg-banana text-banana-foreground font-semibold py-3 rounded-lg hover:bg-banana/90 transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-60">
                      {status === 'loading' ? 'Sending...' : 'Send'}
                      {status !== 'loading' && (
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="m22 2-7 20-4-9-9-4Z" />
                          <path d="M22 2 11 13" />
                        </svg>
                      )}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );

}